import React, { useEffect, useId, useRef } from 'react'
import '../styles/Modal.scss'

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title: string
    children: React.ReactNode
    size?: 'sm' | 'md' | 'lg'
    /**
     * Backdrop clicks are opt-in. A number of shared modals contain destructive
     * actions or a secret that is shown once, so accidental dismissal is unsafe.
     */
    closeOnBackdrop?: boolean
    /** Disable Escape for flows which require an explicit acknowledgement. */
    closeOnEscape?: boolean
    /** Hide the header close control for one-time secrets until they are acknowledged. */
    showCloseButton?: boolean
}

const focusableSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
].join(', ')

const openModalStack: HTMLElement[] = []
let scrollLockCount = 0

const getFocusableElements = (container: HTMLElement) =>
    Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (element) => element.getClientRects().length > 0 && !element.hasAttribute('inert')
    )

const lockDocumentScroll = () => {
    scrollLockCount += 1
    if (scrollLockCount > 1) return

    document.documentElement.classList.add('modal-open')
    document.body.classList.add('modal-open')
}

const unlockDocumentScroll = () => {
    scrollLockCount = Math.max(0, scrollLockCount - 1)
    if (scrollLockCount > 0) return

    document.documentElement.classList.remove('modal-open')
    document.body.classList.remove('modal-open')
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    closeOnBackdrop = false,
    closeOnEscape = true,
    showCloseButton = true,
}) => {
    const modalRef = useRef<HTMLDivElement>(null)
    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const onCloseRef = useRef(onClose)
    const closeOnEscapeRef = useRef(closeOnEscape)
    const titleId = useId()

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    // Submission flows can temporarily disable Escape while the modal remains open.
    // Keep that changing behavior out of the open/close lifecycle so focus and scroll
    // ownership are not released midway through a request.
    closeOnEscapeRef.current = closeOnEscape

    useEffect(() => {
        const modal = modalRef.current
        if (!isOpen || !modal) return

        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null

        openModalStack.push(modal)
        lockDocumentScroll()

        const moveInitialFocus = () => {
            const autofocusElement = modal.querySelector<HTMLElement>('[data-modal-initial-focus], [autofocus]')
            const bodyFocusable = getFocusableElements(modal).find((element) => !element.classList.contains('modal-close'))
            const target = autofocusElement || bodyFocusable || closeButtonRef.current || modal
            target.focus({ preventScroll: true })
        }

        const frame = window.requestAnimationFrame(moveInitialFocus)

        const handleKeydown = (event: KeyboardEvent) => {
            if (openModalStack[openModalStack.length - 1] !== modal) return

            if (event.key === 'Escape' && closeOnEscapeRef.current) {
                event.preventDefault()
                onCloseRef.current()
                return
            }

            if (event.key !== 'Tab') return

            const focusableElements = getFocusableElements(modal)
            if (focusableElements.length === 0) {
                event.preventDefault()
                modal.focus({ preventScroll: true })
                return
            }

            const activeElement = document.activeElement as HTMLElement | null
            const firstElement = focusableElements[0]
            const lastElement = focusableElements[focusableElements.length - 1]

            if (!activeElement || !focusableElements.includes(activeElement)) {
                event.preventDefault()
                ;(event.shiftKey ? lastElement : firstElement).focus({ preventScroll: true })
                return
            }

            if (event.shiftKey && activeElement === firstElement) {
                event.preventDefault()
                lastElement.focus({ preventScroll: true })
            } else if (!event.shiftKey && activeElement === lastElement) {
                event.preventDefault()
                firstElement.focus({ preventScroll: true })
            }
        }

        document.addEventListener('keydown', handleKeydown)

        return () => {
            window.cancelAnimationFrame(frame)
            document.removeEventListener('keydown', handleKeydown)
            const stackIndex = openModalStack.lastIndexOf(modal)
            if (stackIndex !== -1) openModalStack.splice(stackIndex, 1)
            unlockDocumentScroll()

            if (previouslyFocused && document.contains(previouslyFocused)) {
                window.requestAnimationFrame(() => previouslyFocused.focus({ preventScroll: true }))
            }
        }
    }, [isOpen])

    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnBackdrop && event.target === event.currentTarget) {
            onClose()
        }
    }

    if (!isOpen) return null

    return (
        <div className="modal-backdrop" onMouseDown={handleBackdropClick}>
            <div ref={modalRef} className={`modal-content modal-size-${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
                <div className="modal-header">
                    <h2 id={titleId}>{title}</h2>
                    {showCloseButton && (
                        <button ref={closeButtonRef} type="button" className="modal-close" onClick={onClose} aria-label={`Close ${title}`}>
                            &times;
                        </button>
                    )}
                </div>
                <div className="modal-body">{children}</div>
            </div>
        </div>
    )
}

export default Modal
